import * as React from "react";

import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { PageHeader } from "@/components/ui/page-header";
import { cn } from "@/lib/utils";

/**
 * Standardized shell for public / marketplace / commerce pages.
 *
 * Centralizes the outer `min-h-screen flex flex-col` wrapper + `<Header/>` +
 * `<main id="main-content">` + `<Footer/>` boilerplate that ~20 pages used to
 * hand-roll (mirrors the proven `LegalLayout` structure). On desktop the global
 * `SidebarShell` chrome (`UnifiedTopBar` + `UnifiedSidebar`) already renders around
 * this; the per-page `Header` self-hides on `lg` and is the mobile nav. This shell
 * changes none of that interplay — it just removes the duplication.
 *
 * `mainAriaLabel` is required because the global skip-link targets `#main-content`
 * (see `app/layout.tsx`) — every page must keep a labelled main landmark.
 */

const WIDTHS = {
  wide: "container", // ~1280–1400 cap — marketplace default
  content: "container max-w-4xl", // 896px — editorial / doc body
  narrow: "container max-w-2xl", // 672px — single column
  form: "container max-w-lg", // 512px — confirmation / wizard cards
} as const;

export interface PublicPageShellProps {
  children: React.ReactNode;
  /** Required — mirrors each page's `<main aria-label>`, feeds the skip-link landmark. */
  mainAriaLabel: string;
  /** Inner content width. Ignored when `bleed`. Default "wide". */
  width?: keyof typeof WIDTHS;
  /** Full-bleed `<main>`, no inner container (hero-band + marketing pages). */
  bleed?: boolean;
  /** When set, renders the unified `<PageHeader>` h1 at the top of the content. */
  title?: string;
  description?: string;
  action?: React.ReactNode;
  breadcrumb?: React.ReactNode;
  /** Render `<Footer/>`. Default true. */
  footer?: boolean;
  /** Drop the standardized vertical padding on the container. */
  disablePadding?: boolean;
  /** Decorative layer rendered behind content (e.g. checkout grid overlays). */
  backdrop?: React.ReactNode;
  /**
   * Escape hatch. In contained mode → appended to the inner container.
   * In `bleed` mode → appended to `<main>` (e.g. centered 404 states).
   */
  className?: string;
}

export function PublicPageShell({
  children,
  mainAriaLabel,
  width = "wide",
  bleed = false,
  title,
  description,
  action,
  breadcrumb,
  footer = true,
  disablePadding = false,
  backdrop,
  className,
}: PublicPageShellProps) {
  const header = title ? (
    <PageHeader
      title={title}
      description={description}
      action={action}
      breadcrumb={breadcrumb}
      className="mb-8"
    />
  ) : null;

  return (
    <div className="flex min-h-screen min-w-0 flex-col bg-background">
      {backdrop}
      <Header />

      <main id="main-content" tabIndex={-1} aria-label={mainAriaLabel} className={cn("flex-1", bleed && className)}>
        {bleed ? (
          <>
            {header ? <div className="container py-8 md:py-10">{header}</div> : null}
            {children}
          </>
        ) : (
          <div className={cn(WIDTHS[width], !disablePadding && "py-8 md:py-10", className)}>
            {header}
            {children}
          </div>
        )}
      </main>

      {footer ? <Footer /> : null}
    </div>
  );
}

export default PublicPageShell;
